import React, { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import OrganizationModal from 'shared/components/OrganizationModal';
import styles from './liveStockCommodity.module.scss';
import LoaderAnimation from 'shared/components/LoaderAnimation';
import { Row, UploadFile, Col } from 'antd';
import { LIVE_STOCK_TITLE } from 'constants/FarmboxConstants/stage0.constants';
import { FormType } from 'enums/FieldTypeEnums';
import CircelUploadComponent from 'shared/components/CircleUploadComponent';
import FieldsRow from 'shared/components/FieldsRow';
import { RcFile } from 'antd/lib/upload';
import { ButtonSize } from 'enums/ButtonEnums';
import { getStage0Data } from './stage0FarmData';
import { LiveStockCommodityModel } from 'models/Farmbox/LiveStock/stage0.model';
import LiveStockService from 'services/Farmbox/LiveStockCommodity/liveStock.service';
import useFilters from 'shared/hooks/useFilters';
import { queryParamsList } from 'views/ActivityDataEntry';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { liveStockCommodityValidationSchema } from './liveStockCommodity.validation';
import useFarmboxRedirections from 'shared/utils/useFarmboxRedirections';
import { CommodityTabsKeys } from 'enums/FarmPageEnum';
import { urlToRcFile } from 'shared/utils/convertIntoBlob';
import { ButtonLabels } from 'enums/CreateBrandModalEnums';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import isProductDisabled from 'shared/utils/isProductDisabled';

interface LiveStockCommodityProps {
  visible: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  fetchCommodityList?: (params: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
    locationId?: string;
    year: string;
  }) => Promise<void>;
  location?: Location; // TODO will use in update call
  size?: number;
  handleGetCommodity?: () => void;
  isFeed?: boolean;
}
const LiveStockCommodity: FC<LiveStockCommodityProps> = props => {
  const { visible, setVisible, handleGetCommodity, isFeed } = props;
  const { redirectToLiveStockGrowthStage } = useFarmboxRedirections();
  const {
    updateLoading,
    loading,
    createLiveStockCommodity,
    getCommodity,
    commodity,
    updatedCommodity,
  } = LiveStockService();
  const { getParams } = useFilters();
  const { activeTab } = getParams(queryParamsList);
  const { id: locationId, status } = useParams();
  const { BEEF_COMMODITY, UPDATE_BEEF_COMMODITY, CREATE_BEEF_COMMODITY } =
    LIVE_STOCK_TITLE;
  const { CONFIRM, UPDATE, CANCEL } = ButtonLabels;

  const fetchCommodity = async () => {
    const initialValues =
      !activeTab && (await getCommodity(Number(locationId)));
    if (initialValues) {
      const fetchAndSetImage = async () => {
        const rcFile = await urlToRcFile(initialValues?.imageUrl ?? '');
        methods.setValue('image', rcFile);
        await methods.trigger('image');
      };
      fetchAndSetImage();
    }
    return initialValues
      ? initialValues
      : {
          ...new LiveStockCommodityModel(),
          year: activeTab,
          locationId,
        };
  };
  const methods = useForm({
    defaultValues: fetchCommodity,
    resolver: yupResolver(liveStockCommodityValidationSchema),
    mode: 'all',
  });
  const isDisabled = isProductDisabled(status);
  const formData = getStage0Data({ isDisabled });
  const handleUpload = async (fielName?: RcFile) => {
    methods.setValue('image', fielName);
    await methods.trigger('image');
  };
  const formComponent = loading ? (
    <div className={styles.loadingContainer}>
      <LoaderAnimation />
    </div>
  ) : (
    <div className={styles.formContainer}>
      <FormProvider {...methods}>
        <Row gutter={[24, 24]}>
          <div className={styles.uploadComponentContainer}>
            <div>
              <CircelUploadComponent
                file={[methods.getValues('image')] as UploadFile[]}
                setImage={handleUpload}
                errorMessage={methods.formState.errors.image?.message}
                isDisabled={isDisabled}
              />
            </div>
          </div>
          <Row gutter={[24, 24]} className={styles.formFieldContainer}>
            <Col span={24}>
              <FieldsRow {...formData} formType={FormType.REACT_HOOK_FORMS} />
            </Col>
          </Row>
        </Row>
      </FormProvider>
    </div>
  );
  const onSubmit = async (data: LiveStockCommodityModel) => {
    if (data?.id) {
      const isSuccess = await updatedCommodity(
        {
          ...data,
          locationId: commodity?.locationId,
        },
        Number(locationId)
      );
      if (isSuccess) {
        handleModalClose();
        redirectToLiveStockGrowthStage(data?.id, CommodityTabsKeys.DRAFT);
        isFeed && handleGetCommodity?.();
      }
    } else {
      const isSuccess = await createLiveStockCommodity({
        ...data,
        categoryId: 1,
      });
      if (isSuccess) {
        handleModalClose();
        redirectToLiveStockGrowthStage(isSuccess, CommodityTabsKeys.DRAFT);
      }
    }
  };
  const handleModalClose = () => setVisible?.(false);
  const footerProps = {
    confirmButtonProps: {
      children: commodity?.id ? UPDATE : CONFIRM,
      primary: true,
      size: ButtonSize.SMALL,
      onClick: methods.handleSubmit(onSubmit),
      loading: updateLoading,
    },
    cancelButtonProps: {
      children: CANCEL,
      secondary: true,
      size: ButtonSize.SMALL,
      onClick: handleModalClose,
    },
    wrapClassName: styles.footerWrapper,
  };
  const modalTitle = isDisabled
    ? BEEF_COMMODITY
    : commodity?.id
    ? UPDATE_BEEF_COMMODITY
    : CREATE_BEEF_COMMODITY;
  return (
    <OrganizationModal
      width={450}
      visible={visible}
      title={modalTitle}
      isExtraSpace
      onClose={handleModalClose}
      modalFooterProps={isDisabled ? undefined : footerProps}
      children={formComponent}
      wrapClassName={styles.farmCommoditymodalContainer}
    />
  );
};

export default LiveStockCommodity;
